import { z } from 'zod/v4';

import { SRMTagDTO } from '@/entities/srm/tag/@x/tql-query';
import { TreeSettingsSchemaDTO } from '@/shared/lib/utils/tree/schema';
import { Position as ReactflowPosition } from 'reactflow'; // or your local definition

// Extract values of enum Position
const PositionValues = Object.values(ReactflowPosition);

// Create Zod enum from those values:
export const PositionDTO = z.enum(PositionValues);
// If you have zod schemas for Edge and Node, import them.
// Here's a minimal generic, you should replace/augment for your actual shape:
const XYPositionDTO = z.object({
  x: z.number(),
  y: z.number(),
});

// CSSProperties as a loose record of strings / numbers / objects
// You might want to import `CSSProperties` typings and create a stricter schema
const CSSPropertiesDTO = z.record(z.string(), z.any()); // loose placeholder

// CoordinateExtent could be 'parent' or [number, number]

export const NodeDTO = z.object({
  id: z.string(),
  position: XYPositionDTO,
  data: z.any(), // fallback for generic T, replace with actual shape if known

  type: z.string().optional().nullable(), // U extends string | undefined

  style: CSSPropertiesDTO.optional(),
  className: z.string().optional(),
  sourcePosition: PositionDTO.optional(),
  targetPosition: PositionDTO.optional(),
  hidden: z.boolean().optional(),
  draggable: z.boolean().optional(),
  selectable: z.boolean().optional(),
  connectable: z.boolean().optional(),
  deletable: z.boolean().optional(),
  dragHandle: z.string().optional(),
  width: z.number().nullish(),
  height: z.number().nullish(),

  parentNode: z.string().optional(), // deprecated - keep optional
  parentId: z.string().optional(),

  zIndex: z.number().optional(),
  extent: z.any().optional(),
  expandParent: z.boolean().optional(),
  positionAbsolute: XYPositionDTO.optional(),
  ariaLabel: z.string().optional(),
  focusable: z.boolean().optional(),
});

// Export type
export type Node = z.infer<typeof NodeDTO>;
// Edge schema
export const EdgeDTO = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.string().nullable().optional(),
  targetHandle: z.string().nullable().optional(),
  type: z.string().optional(),
  style: z.record(z.string(), z.any()).optional(), // CSSProperties as generic object
  className: z.string().optional(),
  animated: z.boolean().optional(),
  label: z.any().optional(), // You can extend to z.union([z.string(), z.any()]) if needed
  labelStyle: z.record(z.string(), z.any()).optional(),
  labelShowBg: z.boolean().optional(),
  ariaLabel: z.string().optional(),
  data: z.any().optional(),
});
export type Edge = z.infer<typeof EdgeDTO>;

export const SearchQueryNodeDataDTO = z.object({
  id: z.string(),
  color: z.string(),
  name: z.string(),
  conditions: z.any().optional(), // Expand when you know the shape
});
export type SearchQueryNodeData = z.infer<typeof SearchQueryNodeDataDTO>;

export const TQLFilterDTO = z.object({
  links: z.boolean(),
  edges: z.array(EdgeDTO),
  nodes: z.array(NodeDTO),
});
export type TQLFilter = z.infer<typeof TQLFilterDTO>;

export const TQLQueryDTO = z.object({
  id: z.string(),
  label: z.string(),
  name: z.string(),
  filter: TQLFilterDTO,
  table_settings: z.array(TreeSettingsSchemaDTO).optional(),
  tags: z.array(SRMTagDTO).optional(),
  can_edit: z.boolean(),
});
export type TQLQuery = z.infer<typeof TQLQueryDTO>;

export const CreateTQLQueryDTO = TQLQueryDTO.omit({
  id: true,
  tags: true,
  can_edit: true,
}).extend({
  tags: z.array(z.string()),
});
export type CreateTQLQuery = z.infer<typeof CreateTQLQueryDTO>;

export const UpdateTQLQueryDTO = CreateTQLQueryDTO.extend({
  id: TQLQueryDTO.shape.id,
  label: z.string().optional(),
});
export type UpdateTQLQuery = z.infer<typeof UpdateTQLQueryDTO>;

export const TQLQueryBriefDTO = TQLQueryDTO.pick({
  id: true,
  label: true,
  name: true,
  tags: true,
});
export type TQLQueryBrief = z.infer<typeof TQLQueryBriefDTO>;
